import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { usePreferences } from '@/store/preferences';
import nextchampLogo from '@/assets/nextchamp-logo-transparent.png';

const slogans: { text: string; style: React.CSSProperties }[] = [
  { text: 'MAKE EVERY PLAY COUNT', style: { top: '3%', left: '3%', transform: 'rotate(-6deg)', fontSize: '1.1rem', opacity: 0.12 } },
  { text: 'NO DAYS OFF', style: { top: '4%', right: '5%', transform: 'rotate(4deg)', fontSize: '0.85rem', opacity: 0.08 } },
  { text: 'GRIND. RISE. REPEAT.', style: { top: '12%', right: '2%', transform: 'rotate(3deg)', fontSize: '1.3rem', opacity: 0.13 } },
  { text: 'STAY HUNGRY', style: { top: '13%', left: '5%', transform: 'rotate(-3deg)', fontSize: '0.75rem', opacity: 0.07 } },
  { text: 'BUILT DIFFERENT', style: { top: '22%', left: '2%', transform: 'rotate(-5deg)', fontSize: '1rem', opacity: 0.1 } },
  { text: 'LOCK IN', style: { top: '24%', right: '6%', transform: 'rotate(6deg)', fontSize: '0.8rem', opacity: 0.06 } },
  { text: 'EARN YOUR SPOT', style: { bottom: '42%', left: '4%', transform: 'rotate(-4deg)', fontSize: '0.9rem', opacity: 0.09 } },
  { text: 'OUTWORK EVERYONE', style: { bottom: '44%', right: '3%', transform: 'rotate(5deg)', fontSize: '1.15rem', opacity: 0.11 } },
  { text: 'PROVE THEM WRONG', style: { bottom: '34%', right: '5%', transform: 'rotate(-3deg)', fontSize: '0.85rem', opacity: 0.07 } },
  { text: 'CHASE GREATNESS', style: { bottom: '36%', left: '3%', transform: 'rotate(4deg)', fontSize: '1.05rem', opacity: 0.1 } },
  { text: 'NO EXCUSES', style: { bottom: '26%', left: '6%', transform: 'rotate(-7deg)', fontSize: '0.8rem', opacity: 0.06 } },
  { text: 'MIND SHARP', style: { bottom: '28%', right: '4%', transform: 'rotate(3deg)', fontSize: '0.95rem', opacity: 0.12 } },
  { text: 'HUSTLE HARDER', style: { bottom: '20%', right: '8%', transform: 'rotate(-4deg)', fontSize: '0.75rem', opacity: 0.08 } },
  { text: 'ALL IN', style: { bottom: '22%', left: '4%', transform: 'rotate(5deg)', fontSize: '1.2rem', opacity: 0.13 } },
];

const Welcome = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage } = usePreferences();
  const [bouncing, setBouncing] = useState(false);

  const handleLogoBounce = () => {
    if (bouncing) return;
    setBouncing(true);
    setTimeout(() => setBouncing(false), 280);
  };

  return (
    <div className="relative mx-auto flex h-[100dvh] max-w-lg flex-col items-center justify-between overflow-hidden" style={{ backgroundColor: '#000000' }}>
      {/* Language switch — top right */}
      <div
        className="absolute right-4 z-20 flex items-center gap-1.5"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + clamp(56px, 9vh, 96px))' }}
      >
        <button
          onClick={() => setLanguage('fr')}
          className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider transition-all duration-200 ${
            language === 'fr'
              ? 'bg-white/15 text-white'
              : 'text-white/40 hover:text-white/70'
          }`}
        >
          FR 🇫🇷
        </button>
        <button
          onClick={() => setLanguage('en')}
          className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider transition-all duration-200 ${
            language === 'en'
              ? 'bg-white/15 text-white'
              : 'text-white/40 hover:text-white/70'
          }`}
        >
          ENG 🇬🇧
        </button>
      </div>

      {/* Decorative background slogans */}
      <div className="pointer-events-none absolute inset-0 z-0 select-none overflow-hidden" aria-hidden="true">
        {slogans.map((s, i) => (
          <span
            key={i}
            className="absolute whitespace-nowrap font-black uppercase tracking-wider"
            style={{
              ...s.style,
              color: '#ffffff',
              fontFamily: "'Permanent Marker', 'Marker Felt', 'Comic Sans MS', cursive",
              lineHeight: 1,
            }}
          >
            {s.text}
          </span>
        ))}
      </div>

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center">
        <img
          src={nextchampLogo}
          alt="Nextchamp logo"
          width={512}
          height={512}
          onClick={handleLogoBounce}
          className={`h-[45vh] max-h-[360px] w-auto cursor-pointer object-contain animate-[welcomeLogo_0.8s_cubic-bezier(0.16,1,0.3,1)_both,logoFloat_4s_ease-in-out_1.5s_infinite] ${bouncing ? 'animate-[logoBounce_0.28s_ease-in-out_forwards]' : ''}`}
          style={{ willChange: 'transform' }}
        />
        <p
          className="px-6 text-center text-xl font-bold tracking-wider text-white animate-[welcomeSloganIn_0.7s_cubic-bezier(0.16,1,0.3,1)_0.6s_both]"
          style={{
            marginTop: '-24px',
            fontFamily: "'Permanent Marker', 'Marker Felt', 'Comic Sans MS', cursive",
            textShadow: '0 0 6px rgba(255,255,255,0.2)',
            lineHeight: 1.4,
          }}
        >
          {t.welcome_slogan}
        </p>
      </div>

      <div className="relative z-10 flex w-full flex-col items-center gap-3 px-8 pb-12">
        <Button
          className="w-full max-w-xs rounded-full bg-primary py-6 text-sm font-bold uppercase tracking-widest text-primary-foreground hover:bg-primary/90 active:scale-[0.97] transition-transform duration-150 animate-[welcomeButtonIn_0.7s_cubic-bezier(0.22,1,0.36,1)_1s_both]"
          onClick={() => navigate('/signup')}
        >
          {t.sign_up}
        </Button>
        <Button
          variant="outline"
          className="w-full max-w-xs rounded-full border-border py-6 text-sm font-bold uppercase tracking-widest text-foreground hover:bg-secondary active:scale-[0.97] transition-transform duration-150 animate-[welcomeButtonIn_0.7s_cubic-bezier(0.22,1,0.36,1)_1.2s_both]"
          onClick={() => navigate('/login')}
        >
          {t.log_in}
        </Button>
      </div>
    </div>
  );
};

export default Welcome;
